import { useState, useCallback, useRef } from "react";
import { Upload, Film, AlertCircle, Loader2, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { compressVideo, getFileSizeMB, shouldCompress } from "../lib/videoCompression";
import BrandLogo from "./BrandLogo";

const UploadView = ({ onUpload, error }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [showCompressOption, setShowCompressOption] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const processFile = useCallback(async (file, compress = false) => {
    if (compress) {
      setIsCompressing(true);
      setCompressionProgress(0);
      try {
        const compressed = await compressVideo(file, setCompressionProgress);
        setIsCompressing(false);
        onUpload(compressed);
      } catch (err) {
        console.error('Compression failed:', err);
        setIsCompressing(false);
        // Fall back to original file
        onUpload(file);
      }
    } else {
      onUpload(file);
    }
  }, [onUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && isValidFile(file)) {
      setValidationError(null);
      if (shouldCompress(file)) {
        setSelectedFile(file);
        setShowCompressOption(true);
      } else {
        processFile(file, false);
      }
    } else if (file) setValidationError("Choose an MP4 or MOV file no larger than 200 MB.");
  }, [processFile]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file && isValidFile(file)) {
      setValidationError(null);
      if (shouldCompress(file)) {
        setSelectedFile(file);
        setShowCompressOption(true);
      } else {
        processFile(file, false);
      }
    } else if (file) setValidationError("Choose an MP4 or MOV file no larger than 200 MB.");
  }, [processFile]);

  const isValidFile = (file) => {
    const validTypes = ["video/mp4", "video/quicktime"];
    const maxSize = 200 * 1024 * 1024; // 200MB
    return validTypes.includes(file.type) && file.size <= maxSize;
  };

  const handleUploadChoice = (compress) => {
    setShowCompressOption(false);
    processFile(selectedFile, compress);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6 md:p-12" data-testid="upload-view">
      <div className="w-full max-w-xl text-center">
        {/* Rezzil Logo */}
        <div className="mb-8">
          <BrandLogo />
        </div>

        {/* Title */}
        <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-white mb-3">
          Analyze your swing with <span className="text-[#CCFF00]">Pro-Shot</span>
        </h1>
        
        <p className="text-[#A3A3A3] text-base md:text-lg mb-10">
          Upload your golf swing video for instant analysis
        </p>

        <Link
          to="/camera"
          className="mb-8 inline-flex items-center gap-2 border border-[#CCFF00] bg-[#CCFF00]/5 px-5 py-3 font-heading text-sm font-bold uppercase tracking-wider text-[#CCFF00] transition-colors hover:bg-[#CCFF00] hover:text-black"
          data-testid="live-camera-link"
        >
          <Camera className="h-5 w-5" />
          Start live-camera session
        </Link>

        {/* Error Message */}
        {(error || validationError) && (
          <div 
            className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30] rounded-sm flex items-center justify-center gap-3"
            data-testid="upload-error"
          >
            <AlertCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0" />
            <span className="text-[#FF3B30] text-sm">{validationError || error}</span>
          </div>
        )}

        {/* Compression in Progress */}
        {isCompressing && (
          <div className="mb-6 p-6 bg-[#121212] border border-[#262626] rounded-sm">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Loader2 className="w-5 h-5 text-[#CCFF00] animate-spin" />
              <span className="text-white font-medium">Compressing video...</span>
            </div>
            <div className="w-full h-2 bg-[#262626] rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#CCFF00] transition-all duration-300"
                style={{ width: `${compressionProgress}%` }}
              />
            </div>
            <p className="text-[#A3A3A3] text-sm mt-2">{compressionProgress}% complete</p>
          </div>
        )}

        {/* Compression Choice Dialog */}
        {showCompressOption && selectedFile && !isCompressing && (
          <div className="mb-6 p-6 bg-[#121212] border border-[#CCFF00] rounded-sm">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Film className="w-6 h-6 text-[#CCFF00]" />
              <span className="text-white font-medium">Large file detected ({getFileSizeMB(selectedFile)} MB)</span>
            </div>
            <p className="text-[#A3A3A3] text-sm mb-4">
              Compressing will speed up upload and processing while maintaining analysis quality.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => handleUploadChoice(true)}
                className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-white transition-colors rounded-sm px-6 py-2 text-sm"
              >
                Compress & Upload
              </button>
              <button
                onClick={() => handleUploadChoice(false)}
                className="bg-transparent border border-[#262626] text-white font-bold uppercase tracking-wider hover:border-[#CCFF00] transition-colors rounded-sm px-6 py-2 text-sm"
              >
                Upload Original
              </button>
            </div>
          </div>
        )}

        {/* Upload Dropzone */}
        {!showCompressOption && !isCompressing && (
          <div
            className={`dropzone rounded-sm p-8 cursor-pointer transition-all duration-300 ${
              isDragging ? "active border-[#CCFF00] bg-[#CCFF00]/5" : "border-[#262626]"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.mov,video/mp4,video/quicktime"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            <div className="flex flex-col items-center">
              <div className="w-14 h-14 mb-4 bg-[#1A1A1A] rounded-full flex items-center justify-center">
                <Upload className="w-7 h-7 text-[#A3A3A3]" />
              </div>
              <p className="font-heading font-semibold text-lg text-white mb-1 uppercase">
                Drop your video here
              </p>
              <p className="text-[#A3A3A3] text-sm mb-3">
                or click to browse
              </p>
              <p className="font-mono text-xs text-[#666] uppercase tracking-wider">
                MP4 or MOV • Max 200MB
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadView;
